import React from "react";
import { PLANS, REGION_PRICES, type RegionPrice } from "./landingContent";
import { useLandingCopy } from "./landingCopy";
import { Emphasis, SectionShell } from "./LandingSections";
import RegionSelect from "./RegionSelect";

/** "$14.99" → "$0", "149.000₫" → "0₫" — the trial price in the region's own format. */
export const formatFreePrice = (price: RegionPrice): string =>
  price.monthly.replace(/[\d.,]+/, "0");

interface PlansSectionProps {
  regionId: string;
  onRegionChange: (id: string) => void;
  onAction: (action: string) => void;
}

const PlansSection: React.FC<PlansSectionProps> = ({
  regionId,
  onRegionChange,
  onAction,
}) => {
  const { tr, headline } = useLandingCopy();
  const price = REGION_PRICES.find((region) => region.id === regionId) ?? REGION_PRICES[0];

  return (
    <SectionShell id="plans">
      <div className="al-head" data-reveal>
        <div className="al-eyebrow al-eyebrow--center">{tr(PLANS.eyebrow)}</div>
        <h2 className="al-h2">
          <Emphasis markup={headline("plansT")} />
        </h2>
        <p className="al-lead">{tr(PLANS.body)}</p>
      </div>

      <div className="al-region-pick" data-reveal>
        <label htmlFor="al-region" id="al-region-label">{tr("Select your region to see Pro pricing")}</label>
        <RegionSelect id="al-region" value={price.id} onChange={onRegionChange} />
      </div>

      <div className="al-plans">
        {PLANS.tiers.map((tier) => {
          const isPro = Boolean(tier.badge);
          return (
            <div
              key={tier.name}
              className={`al-plan ${isPro ? "al-plan--pro" : ""}`}
              data-reveal
            >
              {tier.badge && <span className="al-plan__badge">{tr(tier.badge)}</span>}
              <span className="al-plan__kicker">{tr(tier.kicker)}</span>
              <h3 className="al-plan__name">{tr(tier.name)}</h3>
              <p className="al-plan__tagline">{tr(tier.tagline)}</p>

              <div className="al-plan__price">
                {isPro ? (
                  <div className="al-price-cols">
                    <div className="al-price-col">
                      <span>{tr("Monthly")}</span>
                      <b>{price.monthly}</b>
                      <small>{tr("per month")}</small>
                    </div>
                    <div className="al-price-col al-price-col--yearly">
                      <em className="al-ribbon">
                        {tr("Save")} {price.save}%
                      </em>
                      <span>{tr("Yearly")}</span>
                      <b>{price.yearly}</b>
                      <small>{tr("per year")}</small>
                    </div>
                  </div>
                ) : (
                  <div className="al-price-cols al-price-cols--single">
                    <div className="al-price-col">
                      <span>{tr("Free trial")}</span>
                      <b>{formatFreePrice(price)}</b>
                      <small>{tr("for 7 days · no card needed")}</small>
                    </div>
                  </div>
                )}
              </div>

              <ul className="al-plan__features">
                {tier.features.map((feature) => (
                  <li key={feature}>{tr(feature)}</li>
                ))}
              </ul>
              <button
                type="button"
                className={`al-btn ${isPro ? "al-btn--primary" : "al-btn--ghost"} al-plan__cta`}
                onClick={() => onAction(tier.cta.action)}
              >
                {tr(tier.cta.label)}
                {isPro ? " ✦" : ""}
              </button>
            </div>
          );
        })}
      </div>
      <p className="al-fineprint" data-reveal>
        {tr(PLANS.fineprint)}
      </p>
    </SectionShell>
  );
};

export default PlansSection;
